import {
  canSplit,
  computeRects,
  countLeaves,
  cycle,
  hasLeaf,
  leafOrder,
  nearestSplit,
  neighbour,
  removeLeaf,
  resetRatio,
  setRatio,
  splitLeaf,
} from '../components/terminal/paneTreeModel';
import { usePreferencesStore } from './preferencesStore';
import { shouldCloseOnExit } from '../utils/terminalCloseOnExit';
import { recordTerminalPane, recordTerminalSplit } from '../services/telemetry/terminalUsage';

/**
 * Pane actions of `useTerminalExtStore` (a slice creator, so the store file
 * stays small). All tree math is in `paneTreeModel`; these only look up the
 * tab, call the model and write the result.
 */
let paneSeq = 0;
export const newPaneId = () => `p_${Date.now().toString(36)}_${(paneSeq += 1)}`;

const RESIZE_STEP = 0.02;

const findTab = (s, tabId) => s.tabs.find((t) => t.id === tabId) || null;
const tabOfPane = (s, paneId) => s.tabs.find((t) => hasLeaf(t.layout, paneId)) || null;
const withMru = (mru, paneId) => [paneId, ...(mru || []).filter((id) => id !== paneId)].slice(0, 16);

/** What a split of `src` opens by default: the same program, in its cwd. */
export function cloneSpec(src) {
  if (!src) return { kind: 'local' };
  const spec = {
    kind: src.kind,
    label: src.label,
    shellKey: src.shellKey,
    connId: src.connId,
    profileId: src.profileId,
    // A split of a RESUMED agent must not resume the same conversation a
    // second time: it gets the launch without the resume args.
    launch: src.freshLaunch || src.launch,
    icon: src.icon,
    cwd: src.liveCwd || src.cwd,
  };
  Object.keys(spec).forEach((k) => spec[k] === undefined && delete spec[k]);
  return spec;
}

export function createPaneActions(set, get, makePane) {
  const patchTab = (tabId, fn) =>
    set((s) => ({ tabs: s.tabs.map((t) => (t.id === tabId ? { ...t, ...fn(t) } : t)) }));

  const patchPane = (paneId, patch) =>
    set((s) => (s.panes[paneId] ? { panes: { ...s.panes, [paneId]: { ...s.panes[paneId], ...patch } } } : {}));

  return {
    /** Grid pixel size per tab (rAF-throttled by the grid) for canSplit/neighbour. */
    gridSizes: {},
    setGridSize: (tabId, w, h) => {
      const cur = get().gridSizes[tabId];
      if (cur && cur.w === w && cur.h === h) return;
      set((s) => ({ gridSizes: { ...s.gridSizes, [tabId]: { w, h } } }));
    },

    /** `{ ok, reason }` for the toolbar/header buttons' disabled state. */
    canSplitPane: (paneId, dir) => {
      const s = get();
      const tab = tabOfPane(s, paneId);
      if (!tab) return { ok: false, reason: 'missing' };
      if (tab.locked) return { ok: false, reason: 'locked' };
      const size = s.gridSizes[tab.id] || {};
      return canSplit(tab.layout, paneId, dir, size.w, size.h);
    },

    /** Split `paneId`; the new pane runs `spec` (default: a clone). Returns its id or null. */
    splitPane: (paneId, dir, spec) => {
      const s = get();
      const tab = tabOfPane(s, paneId);
      if (!tab || !get().canSplitPane(paneId, dir).ok) return null;
      const pane = makePane(tab.id, spec || cloneSpec(s.panes[paneId]));
      set((st) => ({
        panes: { ...st.panes, [pane.id]: pane },
        tabs: st.tabs.map((t) =>
          t.id === tab.id
            ? {
                ...t,
                layout: splitLeaf(t.layout, paneId, dir, pane.id),
                focusedPaneId: pane.id,
                zoomedPaneId: null,
                mru: withMru(t.mru, pane.id),
              }
            : t
        ),
      }));
      recordTerminalSplit();
      recordTerminalPane(pane);
      return pane.id;
    },

    /** Close a pane; the last pane closes its tab. */
    closePane: (paneId) => {
      const s = get();
      const tab = tabOfPane(s, paneId);
      if (!tab) return;
      if (countLeaves(tab.layout) <= 1) {
        get().closeTab(tab.id);
        return;
      }
      const { tree, focus } = removeLeaf(tab.layout, paneId);
      set((st) => {
        const panes = { ...st.panes };
        delete panes[paneId];
        return {
          panes,
          tabs: st.tabs.map((t) => {
            if (t.id !== tab.id) return t;
            const mru = (t.mru || []).filter((id) => id !== paneId);
            const nextFocus = t.focusedPaneId === paneId ? mru.find((id) => hasLeaf(tree, id)) || focus : t.focusedPaneId;
            return {
              ...t,
              layout: tree,
              focusedPaneId: nextFocus,
              zoomedPaneId: null,
              broadcast: countLeaves(tree) > 1 ? t.broadcast : false,
              mru,
            };
          }),
        };
      });
    },

    /** Keep only `paneId` in its tab. */
    closeOtherPanes: (paneId) => {
      const tab = tabOfPane(get(), paneId);
      if (!tab) return;
      leafOrder(tab.layout)
        .filter((id) => id !== paneId)
        .forEach((id) => get().closePane(id));
    },

    focusPane: (paneId) => {
      const tab = tabOfPane(get(), paneId);
      if (!tab || tab.focusedPaneId === paneId) return;
      patchTab(tab.id, (t) => ({ focusedPaneId: paneId, mru: withMru(t.mru, paneId) }));
    },

    /** Move focus to the geometric neighbour ('left'|'right'|'up'|'down'). */
    focusNeighbour: (tabId, direction) => {
      const s = get();
      const tab = findTab(s, tabId);
      if (!tab) return;
      const size = s.gridSizes[tabId] || { w: 1000, h: 600 };
      const { panes } = computeRects(tab.layout, size.w, size.h);
      const next = neighbour(panes, tab.focusedPaneId, direction, tab.mru);
      if (!next) return;
      patchTab(tabId, (t) => ({ focusedPaneId: next, zoomedPaneId: null, mru: withMru(t.mru, next) }));
    },

    cycleFocus: (tabId, delta) => {
      const tab = findTab(get(), tabId);
      if (!tab) return;
      const next = cycle(tab.layout, tab.focusedPaneId, delta);
      if (next) patchTab(tabId, (t) => ({ focusedPaneId: next, mru: withMru(t.mru, next) }));
    },

    toggleZoom: (tabId, paneId) => {
      const tab = findTab(get(), tabId);
      if (!tab || countLeaves(tab.layout) < 2) return;
      const target = paneId || tab.focusedPaneId;
      patchTab(tabId, (t) => ({
        zoomedPaneId: t.zoomedPaneId ? null : target,
        focusedPaneId: t.zoomedPaneId ? t.focusedPaneId : target,
      }));
    },

    setRatio: (tabId, splitId, r) => patchTab(tabId, (t) => ({ layout: setRatio(t.layout, splitId, r) })),
    resetRatio: (tabId, splitId) => patchTab(tabId, (t) => ({ layout: resetRatio(t.layout, splitId) })),

    /** Nudge the nearest divider of the focused pane in an arrow's direction. */
    resizeFocused: (tabId, direction, step = RESIZE_STEP) => {
      const tab = findTab(get(), tabId);
      if (!tab) return;
      const dir = direction === 'left' || direction === 'right' ? 'row' : 'col';
      const s = nearestSplit(tab.layout, tab.focusedPaneId, dir);
      if (!s) return;
      const delta = direction === 'right' || direction === 'down' ? step : -step;
      patchTab(tabId, (t) => ({ layout: setRatio(t.layout, s.id, s.ratio + delta), zoomedPaneId: null }));
    },

    /** Broadcast input; refused for the AI tab and for a one-pane tab. */
    setBroadcast: (tabId, on) => {
      const tab = findTab(get(), tabId);
      if (!tab || tab.locked) return false;
      if (on && countLeaves(tab.layout) < 2) return false;
      patchTab(tabId, () => ({ broadcast: !!on }));
      return true;
    },

    /** Restart an exited pane's program in the same xterm. */
    restartPane: (paneId) => {
      const p = get().panes[paneId];
      if (!p || p.ai) return;
      patchPane(paneId, { status: 'starting', exitCode: null, error: null, restartNonce: (p.restartNonce || 0) + 1 });
    },

    /** Replace an exited pane's program with the default shell, same cwd. */
    openShellInPane: (paneId) => {
      const p = get().panes[paneId];
      if (!p || p.ai) return;
      patchPane(paneId, {
        kind: 'local',
        shellKey: undefined,
        connId: undefined,
        profileId: undefined,
        launch: undefined,
        title: undefined,
        label: 'Terminal',
        cwd: p.liveCwd || p.cwd,
        status: 'starting',
        exitCode: null,
        error: null,
        restartNonce: (p.restartNonce || 0) + 1,
      });
    },

    /** The one place exits are recorded — and where "close on exit" applies. */
    setPaneStatus: (paneId, status, code = null, error = null) => {
      const pane = get().panes[paneId];
      const mode = usePreferencesStore.getState().terminalCloseOnExit;
      if (shouldCloseOnExit(mode, pane, status, code, error)) {
        get().closePane(paneId);
        return;
      }
      patchPane(paneId, { status, exitCode: status === 'exited' ? code : null, error });
    },
    setPaneTitle: (paneId, title) => {
      const p = get().panes[paneId];
      if (p && p.title !== title) patchPane(paneId, { title });
    },
    /** 'attached' | 'server_off' | null — from terminal_create. */
    setPaneMcp: (paneId, mcp) => {
      const p = get().panes[paneId];
      if (p && p.mcp !== mcp) patchPane(paneId, { mcp });
    },
    setPaneCwd: (paneId, liveCwd) => {
      const p = get().panes[paneId];
      if (p && p.liveCwd !== liveCwd) patchPane(paneId, { liveCwd });
    },

    /**
     * Replace a tab's layout with `tree`, whose leaves are either existing
     * pane ids of the tab or ids of `newPanes` (specs with an `id`). Panes of
     * the tab not in the tree are closed. Used by presets and saved layouts.
     */
    applyTree: (tabId, tree, newPanes = []) => {
      const tab = findTab(get(), tabId);
      if (!tab || tab.locked || !tree) return;
      const keep = new Set(leafOrder(tree));
      set((st) => {
        const panes = { ...st.panes };
        leafOrder(tab.layout).forEach((id) => !keep.has(id) && delete panes[id]);
        newPanes.forEach((spec) => {
          panes[spec.id] = makePane(tabId, spec);
        });
        const order = leafOrder(tree);
        const focus = keep.has(tab.focusedPaneId) ? tab.focusedPaneId : order[0];
        return {
          panes,
          tabs: st.tabs.map((t) =>
            t.id === tabId
              ? { ...t, layout: tree, focusedPaneId: focus, zoomedPaneId: null, broadcast: order.length > 1 && t.broadcast, mru: [focus] }
              : t
          ),
        };
      });
    },
  };
}
